<template>
  <v-container v-if="initialized">
    <h2>{{ $t('root_cause_archive') }}</h2>
    <v-sheet outlined rounded class="px-4 py-2">
      <div class="text-subtitle-1">{{ $t('filter_by') }}</div>
      <v-row dense>
        <v-col xl="3" lg="3" md="6" cols="12">
          <v-text-field
            :label="$t('route_name')"
            v-model="searchCriteria.route_name"
            @keyup.enter.native="initiateSearch()"
            prepend-inner-icon="mdi-road-variant">
          </v-text-field>
        </v-col>
        <v-col class="d-flex align-center" xl="3" lg="3" md="6" cols="12">
          <date-picker
            v-model="searchCriteria.startedAfter"
            :timezone="customerTimezone"
            :label="$t('start_date')"
            prepend-inner-icon="mdi-calendar"
            reset-on-timezone-change />
          <date-picker
            v-model="searchCriteria.startedBefore"
            :timezone="customerTimezone"
            prepend-inner-icon="mdi-calendar"
            :label="$t('end_date')"
            reset-on-timezone-change
            end-of-day />
        </v-col>
        <v-col xl="3" lg="3" md="6" cols="12">
          <vf-autocomplete
            small
            :label="$tc('vehicle', 2)"
            v-model="searchCriteria.vehicles"
            :items="vehicleOptions"
            prepend-inner-icon="mdi-truck"/>
        </v-col>
        <v-col xl="3" lg="3" md="6" cols="12">
          <v-select
            clearable
            :label="$tc('cause', 2)"
            v-model="searchCriteria.customer_cause"
            :items="customerCauseOptions"
            prepend-inner-icon="mdi-clipboard-text-search"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col xl="3" lg="3" md="6" cols="12">
          <v-select
            clearable
            :label="$tc('product_status', 2)"
            v-model="searchCriteria.product_ok"
            :items="productStatusOptions"
            prepend-inner-icon="mdi-check-underline-circle"/>
        </v-col>
        <v-col xl="3" lg="3" md="6" cols="12">
          <v-select
            clearable
            :label="$tc('action', 2)"
            v-model="searchCriteria.customer_action"
            :items="customerActionOptions"
            prepend-inner-icon="mdi-gesture-tap"/>
        </v-col>
        <v-col xl="3" lg="3" md="6" cols="12">
          <vf-autocomplete
            small
            :label="$tc('violation', 2)"
            v-model="searchCriteria.violations"
            :items="violationOptions"
            prepend-inner-icon="mdi-alert"/>
        </v-col>
        <v-col xl="3" lg="3" md="6" cols="12">
          <v-select
            :label="$t('only_late_violations')"
            v-model="searchCriteria.excludeOnTimeViolations"
            :items="lateViolationOptions"
            prepend-inner-icon="mdi-trash-can"/>
        </v-col>
      </v-row>
      <div class="d-flex justify-end mt-1 mb-2">
        <v-btn outlined color="primary" @click="initiateSearch()" :loading="remoteData.isBusy">
          <v-icon class="mr-1 ml-n1">mdi-magnify</v-icon>
          {{ $t('search') }}
        </v-btn>
        <v-btn outlined color="primary" class="ml-2" @click="resetTable()" :disabled="tableControlsMatchDefaults">
          <v-icon class="mr-1 ml-n1">mdi-filter-remove</v-icon>
          {{ $t('reset') }}
        </v-btn>
      </div>
    </v-sheet>
    <div class="d-flex justify-space-between align-center mt-2">
      <div class="text-subtitle-1 font-weight-bold hidden-md-and-down">{{ $tc('result', 2) }}</div>
      <table-pagination-controls
        :total-rows="remoteData.totalRows"
        :current-page.sync="remoteData.currentPage"
        :rows-per-page.sync="remoteData.rowsPerPage"
        @update:currentPage="refreshResults"
        @update:rowsPerPage="initiateSearch"
        :per-page-options="remoteData.pageOptions"/>
      <div>
        <v-btn
          outlined
          color="primary"
          class="hidden-md-and-down ml-2"
          @click="downloadRemoteDataCsv()"
          :loading="remoteData.isDownloading">
          <v-icon class="mr-1 ml-n1">mdi-tray-arrow-down</v-icon>
          {{ $t('download') }}
        </v-btn>
        <vf-tooltip :text="$t('download')">
          <v-btn class="hidden-lg-and-up" fab text color="primary" @click="downloadRemoteDataCsv()" :loading="remoteData.isDownloading">
            <v-icon>mdi-tray-arrow-down</v-icon>
          </v-btn>
        </vf-tooltip>
      </div>
    </div>
    <div class="data-table-wrapper" :class="{'loading': remoteData.isBusy}">
      <v-data-table
        hide-default-footer
        class="vf-table mt-2"
        :headers="tableFields"
        :items="remoteData.items"
        :server-items-length="remoteData.totalRows"
        :sort-by.sync="remoteData.sortBy"
        :sort-desc.sync="remoteData.sortDesc"
        :loading="remoteData.isBusy"
        :loading-text="$t('loading')"
        @update:sort-by="initiateSearch()"
        @update:sort-desc="initiateSearch()">

        <template v-slot:item.route_name="row">
          <router-link :to="`/route/${row.item.route.id}`">
            {{ row.item.route.name }}
          </router-link>
        </template>

        <template v-slot:item.vehicle="row">
          <router-link v-if="row.item.route.vehicle != null" :to="`/vehicle/${row.item.route.vehicle.id}`">
            {{row.item.route.vehicle.name}}
          </router-link>
          <a v-else
            class="font-italic"
            target="_blank"
            :href="`https://${zendeskDomain}/hc/en-us/articles/360042724133-Vehicle-Cannot-Be-Displayed`">
            {{ $t('cannot_be_displayed') }}
          </a>
        </template>
        <template v-slot:item.route_start="row">
          <span class="text-no-wrap">
            <multi-time-display :time="row.item.route.start" />
          </span>
        </template>
        <template v-slot:item.route_end="row">
          <if-set :value="row.item.route.end" dash>
            <span class="text-no-wrap">
              <multi-time-display :time="row.item.route.end" />
            </span>
            <template v-slot:not-set>
                <span class="grey--text lighten-2">
                  ({{ $t('in_progress') }})
                </span>
            </template>
          </if-set>
        </template>
        <template v-slot:item.customer_cause="row">
          <if-set :value="row.value" dash>
            {{ formatOption(row.value, customerCauseOptions) }}
          </if-set>
        </template>
        <template v-slot:item.product_ok="row">
          <if-set :value="row.value" dash>
            {{ formatOption(row.value, productStatusOptions) }}
          </if-set>
        </template>
        <template v-slot:item.user="row">
          <if-set :value="row.value.email" dash>
            {{ row.value.email }}
          </if-set>
        </template>
        <template v-slot:item.created="row">
          <if-set :value="row.value" dash>
            <multi-time-display class="text-nowrap" :time="row.value" />
          </if-set>
        </template>
        <template v-slot:item.customer_action="row">
          <if-set :value="row.value" dash>
            {{ formatOption(row.value, customerActionOptions) }}
          </if-set>
        </template>
        <template v-slot:item.notes="row">
          <if-set :value="mostRecentNote(row.item)">
            <template v-slot:default="scope">
              <v-tooltip top open-delay="100">
                <template v-slot:activator="{ on, attrs }">
                  <a class="primary--text" v-bind="attrs" v-on="on">
                    {{ getNotePreview(scope.value) }}
                  </a>
                </template>
                <span v-html="getNoteTooltip(scope.value)"/>
              </v-tooltip>
            </template>
            <template v-slot:not-set>
              <span class="grey--text lighten-2">
                ({{ $t('none') }})
              </span>
            </template>
          </if-set>
        </template>
        <template v-slot:item.cooler_status="row">
          <router-link :to="`/route/${row.item.route.id}`">
            <span v-for="(result, index) in formatViolations(row.item.route.route_violations, 'cooler')" :key="index">
              <vf-tooltip :text="result.tooltip">
                <v-icon :color="result.color">{{ result.icon }}</v-icon>
              </vf-tooltip>
            </span>
          </router-link>
        </template>
        <template v-slot:item.freezer_status="row">
          <router-link :to="`/route/${row.item.route.id}`">
            <span v-for="(result, index) in formatViolations(row.item.route.route_violations, 'freezer')" :key="index">
              <vf-tooltip :text="result.tooltip">
                <v-icon :color="result.color">{{ result.icon }}</v-icon>
              </vf-tooltip>
            </span>
          </router-link>
        </template>
        <template v-slot:item.rca_edit="row">
          <vf-tooltip :text="$t('edit')">
            <router-link :to="`/root-cause/${row.item.id}`">
              <v-icon color="primary">mdi-pencil</v-icon>
            </router-link>
          </vf-tooltip>
        </template>
      </v-data-table>
    </div>
  </v-container>
  <v-container v-else>
    <index-table-skeleton>
      <template v-slot:header>
        <h2>{{ $t('root_cause_archive') }}</h2>
      </template>
    </index-table-skeleton>
  </v-container>
</template>

<script>
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import DatePicker from '@/components/shared/DatePicker'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import VfAutocomplete from '@/components/shared/VfAutocomplete'
import VfTooltip from '@/components/shared/VfTooltip'
import IndexTableSkeleton from '@/components/shared/IndexTableSkeleton'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import QueryParamMixin from '@/components/mixins/QueryParamMixin'
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import IfSet from '@/components/shared/IfSet'
import StringUtil from '@/util/StringUtil'

import { each, filter, map, sortBy, find, uniqBy, isEmpty, truncate, some } from 'lodash'

export default {
  name: 'RootCauseIndex',
  components: {
    TablePaginationControls,
    DatePicker,
    MultiTimeDisplay,
    VfAutocomplete,
    VfTooltip,
    IfSet,
    IndexTableSkeleton
  },
  mixins: [
    CurrentUserMixin,
    RemoteDataTableMixin,
    DateTimeUtilsMixin,
    BrowserUtilitiesMixin,
    QueryParamMixin
  ],
  data () {
    return {
      model: 'RouteAssessment',
      remoteData: {
        sortBy: 'id',
        sortDesc: true
      },
      lateViolationOptions: [
        { text: this.$t('exclude_on_time_rcas'), value: 'true' },
        { text: this.$t('show_all'), value: 'false' }
      ],
      searchCriteria: this.getDefaultSearchCriteria(),
      vehicles: [],
      customerAssessmentTerms: [],
      find: find,
      tableFields: [
        {
          value: 'route_name',
          text: this.$tc('route'),
          sortable: true
        },
        {
          value: 'vehicle',
          text: this.$tc('vehicle'),
          sortable: false
        },
        {
          value: 'route_start',
          text: this.$tc('route_start'),
          sortable: true
        },
        {
          value: 'route_end',
          text: this.$tc('route_end'),
          sortable: true
        },
        {
          value: 'customer_cause',
          text: this.$tc('cause'),
          sortable: false
        },
        {
          value: 'product_ok',
          text: 'Status',
          sortable: false
        },
        {
          value: 'user',
          text: this.$tc('assessor'),
          sortable: false
        },
        {
          value: 'created',
          text: this.$tc('assessment_date'),
          sortable: true
        },
        {
          value: 'customer_action',
          text: this.$tc('action', 2),
          sortable: false
        },
        {
          value: 'notes',
          text: this.$tc('note', 2),
          sortable: false
        },
        {
          value: 'cooler_status',
          text: this.$t('cooler'),
          sortable: false
        },
        {
          value: 'freezer_status',
          text: this.$t('freezer'),
          sortable: false
        },
        {
          value: 'rca_edit',
          text: '',
          sortable: false
        }
      ],
      queryParams: [
        {
          name: 'route_name',
          type: String
        },
        {
          name: 'startedAfter',
          type: 'Moment'
        },
        {
          name: 'startedBefore',
          type: 'Moment'
        },
        {
          name: 'vehicles',
          type: 'IDArray',
          validator: value => some(this.vehicleOptions, x => x.value === value)
        },
        {
          name: 'customer_cause',
          type: 'Integer',
          validator: value => some(this.customerCauseOptions, x => x.value === value)
        },
        {
          name: 'product_ok',
          type: 'Integer',
          validator: value => some(this.productStatusOptions, x => x.value === value)
        },
        {
          name: 'customer_action',
          type: 'Integer',
          validator: value => some(this.customerActionOptions, x => x.value === value)
        },
        {
          name: 'violations',
          type: Array,
          validator: value => some(this.violationOptions, x => x.value === value)
        }
      ]
    }
  },
  methods: {
    /**
     * Fetch the vehicles used in the vehicle options filter
     */
    async fetchVehicles () {
      try {
        const response = await this.$orm('Vehicle').findAll({
          customer_id: this.customerContext.id,
          sort: 'name',
          'per-page': 0
        })
        this.vehicles = response.getItems()
      } catch (error) {
        this.handleError(error)
      }
    },
    /**
     * Fetch the customer assessment terms used in the filters
     */
    async fetchCustomerAssessmentTerms () {
      try {
        let response = await this.$orm('CustomerAssessmentTerm').findAll({ customer_id: this.customerContext.id })
        // If the customer didn't have any terms, try the parent customer.
        if (isEmpty(response.getItems()) && this.customerContext.parent_id != null) {
          response = await this.$orm('CustomerAssessmentTerm').findAll({ customer_id: this.customerContext.parent_id })
        }
        this.customerAssessmentTerms = response.getItems()
      } catch (error) {
        this.handleError(error)
      }
    },
    /**
     * Returns the default search criteria
     * @return Object
     */
    getDefaultSearchCriteria () {
      return {
        route_name: null,
        vehicles: [],
        start: null,
        end: null,
        customer_cause: null,
        product_ok: null,
        customer_action: null,
        violations: [],
        excludeOnTimeViolations: 'false'
      }
    },
    /**
     * Sets our data table back to its original state before the user-applied filtering and paging changes
     * @return undefined
     */
    resetTable () {
      this.searchCriteria = this.getDefaultSearchCriteria()
      this.resetRemoteDataTableNavigation()
      this.resetQueryParams()
      this.refreshResults()
    },
    /**
     * Constructs an object of key value pairs that represent the current search criteria
     * @return object
     */
    buildSearchCriteria () {
      const fields = [
        'id',
        'customer_action',
        'customer_cause',
        'product_ok',
        'notes',
        'created',
        'route.route_violations.zone',
        'route.route_violations.type',
        'user.id',
        'user.email',
        'route.id',
        'route.name',
        'route.start',
        'route.end',
        'route.vehicle.id',
        'route.vehicle.name'
      ]
      const searchCriteria = {
        expand: 'route.route_violations,route.vehicle,user,notes',
        fields: fields.join(','),
        customer_id: this.customerContext.id,
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage
      }
      if (this.searchCriteria.route_name != null) {
        searchCriteria.route_name = this.searchCriteria.route_name
      }
      if (this.searchCriteria.vehicles.length > 0) {
        searchCriteria.vehicle_id = this.searchCriteria.vehicles.join(',')
      }
      if (this.searchCriteria.startedAfter != null) {
        searchCriteria.start_after = this.searchCriteria.startedAfter
      }
      if (this.searchCriteria.startedBefore != null) {
        searchCriteria.start_before = this.searchCriteria.startedBefore
      }
      if (this.searchCriteria.customer_cause != null) {
        searchCriteria.customer_cause = this.searchCriteria.customer_cause
      }
      if (this.searchCriteria.product_ok != null) {
        searchCriteria.product_ok = this.searchCriteria.product_ok.value
      }
      if (this.searchCriteria.customer_action != null) {
        searchCriteria.customer_action = this.searchCriteria.customer_action
      }
      if (this.searchCriteria.violations.length > 0) {
        searchCriteria.violations = this.searchCriteria.violations.join(',')
      }
      if (this.searchCriteria.excludeOnTimeViolations != null) {
        searchCriteria.only_late_violations = this.searchCriteria.excludeOnTimeViolations
      }
      if (this.remoteData.sortBy !== null) {
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.remoteData.sortBy}`
      }
      return searchCriteria
    },
    /**
     * When the customer changes, update the page
     * @param newCustomerContext
     * @param oldCustomerContext
     */
    customerContextChangeHandler (newCustomerContext, oldCustomerContext) {
      this.fetchCustomerAssessmentTerms()
      this.fetchVehicles()
      this.resetTable()
    },
    /**
     * Format customer terms options
     * @param id
     * @param optionArray
     * @returns {*|string}
     */
    formatOption (id, optionArray) {
      const item = find(optionArray, (option) => option.value === id)
      return item != null ? item.text : 'N/A'
    },
    /**
     * Depending on the violation type and zone, create presentation data for the violation icons.
     * @param violations
     * @param zone
     * @returns {Array}
     */
    formatViolations (violations, zone) {
      const output = []
      violations = filter(violations, (violation) => {
        return violation.zone === zone && violation.level !== 'warning'
      })
      each(violations, (violation) => {
        if (violation.type === RouteViolationRecord.TYPE_NODATA) {
          output.push({ icon: 'mdi-cancel', color: 'danger', tooltip: this.$t('fatal_violation:no-data') })
        }
        if (violation.type === RouteViolationRecord.TYPE_HOT) {
          output.push({ icon: 'mdi-fire', color: 'danger', tooltip: this.$t('fatal_violation:hot') })
        }
        if (violation.type === RouteViolationRecord.TYPE_COLD) {
          output.push({ icon: 'mdi-snowflake', color: 'primary', tooltip: this.$t('fatal_violation:cold') })
        }
        if (violation.type === RouteViolationRecord.TYPE_GAP) {
          output.push({ icon: 'mdi-clock', color: 'warning', tooltip: this.$t('fatal_violation:time-gap') })
        }
      })
      if (output.length === 0) {
        output.push({ icon: 'mdi-check', color: 'success', tooltip: this.$t('no_violations') })
      }
      return uniqBy(output, x => x.icon)
    },
    /**
     * Returns the most recent note in the notes array
     *
     * @return undefined|string
     */
    mostRecentNote (routeAssessment) {
      const notesArray = routeAssessment.sortedNotes(['frontend/form/root-cause'])
      return notesArray.length > 0 ? notesArray[0].note : undefined
    },
    /**
     * Convenience method to get the stripped form of the note, so we can display it in a tooltip without the
     * automatically enclosing <p> tags messing up spacing (or having to hack around that)
     *
     * @param note
     * @returns {*}
     */
    getNoteTooltip (note) {
      return StringUtil.stripHtml(note)
    },
    /**
     * Returns a truncated version of the note for display inside the table
     * @param note
     * @returns {string|string}
     */
    getNotePreview (note) {
      return truncate(StringUtil.stripHtml(note), { length: 15 }) || ''
    }
  },
  computed: {
    zendeskDomain () {
      return process.env.VUE_APP_ZENDESK_DOMAIN
    },
    /**
     * Options property for customer cause picker
     */
    customerCauseOptions () {
      const options = filter(this.customerAssessmentTerms, term => term.type === 'cause')
      return map(options, (term) => {
        return { text: term.value, value: term.id }
      })
    },
    /**
     * Options property for customer action picker
     */
    customerActionOptions () {
      const options = filter(this.customerAssessmentTerms, term => term.type === 'action')
      return map(options, (term) => {
        return { text: term.value, value: term.id }
      })
    },
    /**
     * Options property for product status picker
     * @returns Object
     */
    productStatusOptions () {
      return [
        { text: this.$t('ok'), value: 1 },
        { text: this.$t('not_ok'), value: 0 }
      ]
    },
    /**
     * Options property for violations picker
     * @returns Object
     */
    violationOptions () {
      return [
        { text: this.$t('fatal_violation:cold'), value: RouteViolationRecord.TYPE_COLD },
        { text: this.$t('fatal_violation:hot'), value: RouteViolationRecord.TYPE_HOT },
        { text: this.$t('fatal_violation:no-data'), value: RouteViolationRecord.TYPE_NODATA },
        { text: this.$t('fatal_violation:time-gap'), value: RouteViolationRecord.TYPE_GAP }
      ]
    },
    /**
     * Options property for vehicle picker
     * @returns [Object]
     */
    vehicleOptions () {
      const vehicleOptions = map(this.vehicles, (vehicle) => {
        return { text: vehicle.name, value: vehicle.id }
      })
      return sortBy(vehicleOptions, v => v.name)
    },
    /**
     * Whether the options have been loaded yet
     * @returns boolean
     */
    initialized () {
      return !isEmpty(this.vehicleOptions) &&
        !isEmpty(this.customerCauseOptions) &&
        !isEmpty(this.customerActionOptions)
    }
  },
  mounted () {
    this.updateBrowserTitle(this.$tc('root_cause_archive', 2))
    this.fetchCustomerAssessmentTerms()
    this.fetchVehicles()
  },
  watch: {
    initialized () {
      this.useQueryParamsForSearch()
      this.initiateSearch()
    }
  }
}
</script>

<style lang="scss" scoped>
// need a little bit less padding in the table so we can cram more stuff in a reasonable sized screen. The dense prop
// doesn't seem to affect the horizontal padding.
.vf-table ::v-deep(th),
.vf-table ::v-deep(td) {
  padding: 0 8px !important;
}
</style>
